import { AppTab } from '../../components/ui/navigation';
import { AppSurface } from './AppWorkflowSurfaces';

const accountSurfaces = new Set<AppSurface>([
  'collections',
  'account',
  'appearance',
  'billing',
  'shared-with-me',
]);
const collectionSurfaces = new Set<AppSurface>([
  'sharing',
  'collection',
  'general',
  'activity',
  'people',
  'roles',
  'danger',
  'transfer',
  'leave',
  'deletion',
  'bin',
]);

export function appDestination(
  tab: AppTab,
  surface?: AppSurface | null,
  collectionId?: string,
): string {
  const base = `/${tab}`;
  if (!surface) return base;
  if (surface === 'buy-credits' || surface === 'transactions')
    return `${base}/billing/${surface}`;
  if (accountSurfaces.has(surface)) return `${base}/${surface}`;
  return collectionId
    ? `${base}/c/${encodeURIComponent(collectionId)}/${surface}`
    : `${base}/collections`;
}

export function parseAppRoute(pathname: string): {
  tab: AppTab;
  surface: AppSurface | null;
  collectionId?: string;
  valid: boolean;
} {
  const parts = pathname.split('/').filter(Boolean);
  const tab: AppTab = parts[0] === 'locations' ? 'locations' : 'items';
  if (pathname === '/') return { tab, surface: null, valid: true };
  if (!['items', 'locations'].includes(parts[0]))
    return { tab, surface: null, valid: false };
  if (parts.length === 1) return { tab, surface: null, valid: true };
  if (
    parts.length === 3 &&
    parts[1] === 'billing' &&
    (parts[2] === 'buy-credits' || parts[2] === 'transactions')
  ) {
    return { tab, surface: parts[2], valid: true };
  }
  if (parts.length === 2 && accountSurfaces.has(parts[1] as AppSurface)) {
    return { tab, surface: parts[1] as AppSurface, valid: true };
  }
  if (
    parts.length === 4 &&
    parts[1] === 'c' &&
    collectionSurfaces.has(parts[3] as AppSurface)
  ) {
    try {
      const collectionId = decodeURIComponent(parts[2]);
      if (
        collectionId &&
        !collectionId.includes('/') &&
        collectionId.length <= 256
      ) {
        return {
          tab,
          surface: parts[3] as AppSurface,
          collectionId,
          valid: true,
        };
      }
    } catch {
      /* Invalid paths resolve to the inventory root. */
    }
  }
  return { tab, surface: null, valid: false };
}

export function parentDestination(
  tab: AppTab,
  surface: AppSurface | null,
  collectionId?: string,
): string {
  if (surface === 'buy-credits' || surface === 'transactions')
    return appDestination(tab, 'billing');
  if (surface === 'roles') return appDestination(tab, 'people', collectionId);
  if (
    ['general', 'activity', 'people', 'danger', 'sharing'].includes(
      surface || '',
    )
  )
    return appDestination(tab, 'collection', collectionId);
  if (['transfer', 'leave', 'deletion'].includes(surface || ''))
    return appDestination(tab, 'danger', collectionId);
  return appDestination(tab);
}
